import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { LogOut, ChevronDown } from 'lucide-react';

/**
 * Shared sidebar primitives used by both the admin and seller shells. Keeping
 * these in one place means the two layouts stay visually identical and gain
 * fixes together. Each layout still owns its own header/posture, since those
 * differ (the admin has the Operativo/Gestione switch, the seller does not).
 */

/** Flatten a grouped nav config (`[{ section, items }]`) into a single list. */
export function flattenItems(nav) {
  const out = [];
  for (const group of nav) {
    if (group.section) {
      for (const it of group.items || []) out.push(it);
    } else {
      out.push(group);
    }
  }
  return out;
}

/** Longest-prefix match so `/admin/ai/personality` wins over `/admin/ai`. */
export function findActive(items, pathname) {
  let match = null;
  for (const it of items) {
    if (it.to && (pathname === it.to || pathname.startsWith(`${it.to}/`))) {
      if (!match || it.to.length > match.to.length) match = it;
    }
  }
  return match;
}

export function NavItem({ item, hasPermission }) {
  if (item.perm && !hasPermission(item.perm)) return null;
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          clsx(
            'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
            isActive
              ? 'bg-sidebar-active text-sidebar-ink font-medium'
              : 'text-sidebar-ink-muted hover:bg-sidebar-elevated hover:text-sidebar-ink'
          )
        }
      >
        {Icon && (
          <Icon
            className="h-4 w-4 shrink-0 text-sidebar-ink-muted group-hover:text-sidebar-ink"
            strokeWidth={1.75}
          />
        )}
        <span>{item.label}</span>
      </NavLink>
    </li>
  );
}

/** Renders permission-filtered nav groups; empty groups are dropped. */
export function NavGroups({ nav, hasPermission, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3" onClick={onNavigate}>
      {nav.map((group, idx) => {
        const visible = (group.items || []).filter((it) => !it.perm || hasPermission(it.perm));
        if (visible.length === 0) return null;
        return (
          <div key={idx} className="mb-4">
            {group.section && (
              <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-sidebar-ink-muted">
                {group.section}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {visible.map((it) => (
                <NavItem key={it.to} item={it} hasPermission={hasPermission} />
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

/** Account avatar + dropdown in the sidebar footer. `accountPath` is the
 *  destination of the account link, which differs per portal. `extra` renders
 *  above the menu (used by the seller shell for the store switcher). */
export function UserMenu({ user, role, accountPath, onLogout, extra }) {
  const [open, setOpen] = useState(false);
  const initials = (user && user.email ? user.email[0] : '?').toUpperCase();
  return (
    <div className="relative">
      {extra && <div className="mb-2">{extra}</div>}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-sidebar-elevated"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-sidebar-ink">{user && user.email}</p>
          <p className="text-[11px] text-sidebar-ink-muted">{role}</p>
        </div>
        <ChevronDown className={clsx('h-3.5 w-3.5 text-sidebar-ink-muted transition-transform', open && 'rotate-180')} strokeWidth={1.75} />
      </button>
      {open && (
        <div className="animate-zoom-in absolute bottom-full left-0 right-0 mb-1 origin-bottom rounded-md border border-border bg-surface-elevated p-1 shadow-elevated">
          <NavLink
            to={accountPath}
            onClick={() => setOpen(false)}
            className="block rounded px-2 py-1.5 text-xs text-ink hover:bg-surface-subtle"
          >
            Account e sicurezza
          </NavLink>
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-ink hover:bg-surface-subtle"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
            Esci
          </button>
        </div>
      )}
    </div>
  );
}
